import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiResponse, PaginatedApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';

/**
 * WorkOrderDto: trimmed wire-format returned by Order.API. Only the fields
 * Cost Analysis consumes are typed here — extra fields on the response are
 * ignored. <c>customer</c> is hydrated server-side so the UI can render
 * client name without a second round-trip against Finance.API's business
 * service.
 */
export interface WorkOrderDto {
  id: string;
  orderNumber?: string | null;
  /** Sierra.OrderApi enum WorkOrder.OrderType (Sample = 0, Bulk = 1, Greige = 2, ShortFall = 3, Linked = 4, BGrade = 5). */
  orderType?: number | null;
  customerReference?: string | null;
  customer?: WorkOrderCustomerDto | null;
}

export interface WorkOrderCustomerDto {
  id: string;
  name: string;
  code?: string | null;
}

@Injectable({ providedIn: 'root' })
export class WorkOrderProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/work-order';

  /**
   * Resolve a list of work-order references (the same value the cost engine
   * persists as <c>WorkOrderKey.reference</c>) into a hydrated DTO that
   * includes the resolved customer. Chunked at 500 ids per request so very
   * large periods stay under Order.API's body-size ceiling. Per-chunk
   * failures degrade silently — the affected WOs simply render without a
   * type chip / client name instead of blocking the dashboard.
   */
  public getByIds(ids: string[]): Observable<ApiResponse<WorkOrderDto[]>> {
    if (ids.length === 0) {
      return of({ success: true, data: [] as WorkOrderDto[], errorMessage: null });
    }

    const CHUNK_SIZE = 500;
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      chunks.push(ids.slice(i, i + CHUNK_SIZE));
    }

    const requests = chunks.map((chunk) =>
      this.httpClient
        .post<ApiResponse<WorkOrderDto[]>>(`${this.baseUrl}/batch/ids`, chunk)
        .pipe(catchError(() => of({ success: false, data: [] as WorkOrderDto[], errorMessage: null }))),
    );

    return forkJoin(requests).pipe(
      map((responses) => {
        const merged: WorkOrderDto[] = [];
        for (const resp of responses) {
          if (resp.success && resp.data) {
            merged.push(...resp.data);
          }
        }
        return { success: true, data: merged, errorMessage: null };
      }),
    );
  }

  public search(query: string, pageSize = 50): Observable<ApiResponse<PaginatedApiResponse<WorkOrderDto>>> {
    const params = new HttpParams().set('pageNumber', 1).set('pageSize', pageSize);
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<WorkOrderDto>>>(
      `${this.baseUrl}/search`,
      { orderNumber: query },
      { params },
    );
  }
}
